import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { ParameterForeignKeyString } from "building-types/doc/foreignKeyString";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { EntityHostedAnnotationPoint } from "building-types/entities/entityHostedAnnotationTypes";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type {
  AnnotationHandle,
  ContentTemplateInstanceHandle,
  RoomLayoutInstanceHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { ParameterRef } from "building-types/options/parameter";
import type { GeometryRef } from "building-types/roofs/roofSystemTypes";
import type { RoomLayoutEligibleDocEntity } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type {
  PositioningMethod,
  AnnotationType,
  AnnotationLineType,
  AnnotationFillType,
  PlacementXAnchor,
  TextAlignment,
  PathEndStyle,
  TextVerticalAlignment,
  PlacementYAnchor,
} from "./annotationTypesEnum";

export type Annotation = Linework | Note | LinearDimension | Detail | DeprecatedPlanDim;

interface AnnotationBase
  extends FormulaDisabledDocEntityBase<"ANNOTATION">,
    RoomLayoutEligibleDocEntity,
    LabelableEntityBase {
  readonly id: PrimaryEntityId<"ANNOTATION">;
  readonly handle?: AnnotationHandle | RoomLayoutInstanceHandle | ContentTemplateInstanceHandle;
  readonly handleAuxiliary?: AnnotationHandle | ContentTemplateInstanceHandle;
  readonly name: string;
  readonly positioningMethod: PositioningMethod;
  readonly edgeId?: string;
  readonly parentId?: DocEntityParentId<
    | "ROOM"
    | "ROOM_LAYOUT"
    | "ELEVATION_VIEW"
    | "SECTION_VIEW"
    | "CONTENT_TEMPLATE"
    | "CONTENT_TEMPLATE_INSTANCE"
    | "LEVEL"
  >;
}

export interface LineworkDefinition {
  readonly type: AnnotationType.LINEWORK;
  readonly path?: PolyLine2;
  readonly lineType: AnnotationLineType;
  readonly fillType: AnnotationFillType;
  readonly lineWeight: number;
  readonly offset: boolean;
  readonly offsetDim: number;
  readonly fillThickness: number;
  readonly offsetLineType: AnnotationLineType;
  readonly offsetFillType: AnnotationFillType;
}

export interface Linework extends AnnotationBase, LineworkDefinition {}

export interface NoteDefinition {
  readonly type: AnnotationType.NOTE;
  readonly x: number;
  readonly y: number;
  readonly landingLineX?: number | null;
  readonly landingLineY?: number | null;
  readonly leaderLineX?: number | null;
  readonly leaderLineY?: number | null;
  readonly text: ParameterForeignKeyString;
  readonly rotation: number; // in degrees
  readonly placementXAnchor: PlacementXAnchor;
  readonly placementYAnchor: PlacementYAnchor;
  readonly textAlignment: TextAlignment;
  readonly verticalAlignment: TextVerticalAlignment;
  readonly textBoxWidth?: SheetInches;
  readonly textBoxHeight?: SheetInches;
  readonly leaderLineEndStyle?: PathEndStyle;

  /**
   * The font size in inches. If not specified, this value is based on NOTE_TEXT_SCALE
   */
  readonly fontSize?: SheetInches;

  /**
   * The length of the leader arrow. If not specified, this value is based on LEADER_ARROW_LENGTH
   */
  readonly leaderArrowLength?: number;

  /**
   * The width of the leader arrow. If not specified, this value is based on LEADER_ARROW_WIDTH
   */
  readonly leaderArrowWidth?: number;
}

export interface Note extends AnnotationBase, NoteDefinition {}

interface DetailDefinitionBase {
  readonly type: AnnotationType.DETAIL;
  readonly x: number;
  readonly y: number;
  readonly rotation: number; // in degrees
}

export interface InlineImageAsset extends AnnotationBase, DetailDefinitionBase {
  readonly detailType: "INLINE_IMAGE";
  readonly width: number;
  readonly height: number;
  readonly url?: string;

  /**
   * Width divided by height of the image asset.
   */
  readonly assetAspectRatio: number;

  /**
   * @deprecated this is a very old field that is no longer needed, but exists
   * in some older homes. It predates the elevation views and allows users to
   * specify that this annotation should be displayed on the front elevation.
   */
  readonly addToFrontElevation?: boolean;
}

export interface AssetCatalogReference extends AnnotationBase, DetailDefinitionBase {
  readonly detailType: "ASSET_REFERENCE";
  readonly assetId?: ForeignEntityId<"ASSET">;

  /**
   * Multiplies the asset's own width and height. A DXF that reports itself as
   * 34 x 22 inches is drawn 34 x 22 at 1, and 68 x 44 at 2. Not a view scale.
   */
  readonly scale: number;
}

export interface QRCode extends AnnotationBase, DetailDefinitionBase {
  readonly detailType: "QR_CODE";
  readonly url: ParameterRef | string;
  readonly size: number; // always square - this is both width and height
}

export type Detail = InlineImageAsset | AssetCatalogReference | QRCode;

export interface LinearDimensionDefinition {
  readonly type: AnnotationType.LINEAR_DIMENSION;
  readonly orientation: "HORIZONTAL" | "VERTICAL" | "ALIGNED";
  readonly lineStartX: number;
  readonly lineStartY: number;
  readonly lineEndX: number;
  readonly lineEndY: number;
  readonly startX: number;
  readonly startY: number;
  readonly endX: number;
  readonly endY: number;
  readonly entityHostedStart?: EntityHostedAnnotationPoint;
  readonly entityHostedEnd?: EntityHostedAnnotationPoint;

  /**
   * The distance from the witness line to the dimension line. If not specified, this value is based on WITNESS_GAP
   */
  readonly witnessGap?: number;

  /**
   * The distance from the dimension line to the extension line. If not specified, this value is based on LINEAR_DIMENSION_EXTENSION
   */
  readonly extension?: number;

  /**
   * The length of the tick mark. If not specified, this value is based on TICK_LEN
   */
  readonly tickLength?: number;

  /**
   * The font size in inches. If not specified, this value is based on NOTE_TEXT_SCALE
   */
  readonly fontSize?: SheetInches;

  /**
   * The dimension value. If not specified, this value is based on the distance between the start and end points
   */
  readonly dimensionValue?: string;

  /**
   * The start/end extensions. If not specified, this value is 0
   */
  readonly startExt?: number;
  readonly endExt?: number;

  /**
   * The prefix and suffix to add to the dimension value
   */
  readonly prefix?: string;
  readonly suffix?: string;

  /**
   * The text to display above
   */
  readonly top?: string;

  /**
   * The text to display below
   */
  readonly bottom?: string;

  /**Display entity hosted lock */
  readonly showEntityHostedLock?: boolean;
}

export interface LinearDimension extends AnnotationBase, LinearDimensionDefinition {}

export enum AnchorPt {
  VISUAL_CENTER = "VISUAL_CENTER",
  ARROW_TIP = "ARROW_TIP",
}

export enum DimLabelPlacementType {
  LEADER = "LEADER",
  EDGE = "EDGE",
}

export interface DeprecatedPlanDim extends AnnotationBase {
  readonly type: AnnotationType.PLAN_DIM;
  readonly polyLine: PolyLine2 | GeometryRef;
}
